// ---------------------------------------------------------------------------
// BrainstormCta — shared primitive behind all five brainstorm entry points.
//
// The block-editor entry (Wave 2, merged as PR cogo-web#136) keeps its own
// BlockPreviewBrainstormCta because its onApply is a concrete store mutation
// (`replaceBlock`). The four remaining entries — chat / wizard / gallery /
// ds-axis — each ship a thin wrapper over this primitive that supplies an
// entry-specific label and an onApply stub.
//
// Stub semantics (Workstream C, minimal): onApply fires a toast with the
// picked candidate's narrative headline + `console.debug` trace. No feature-
// slice mutation lands until Workstream D upgrades the provider from mock
// fixtures to real `findExamples` output and real domain effects are wired
// in. The stub surface is deliberate: it exercises the full open-drawer /
// pick / close cycle through the real zustand store so a click-through user
// test in preview proves the pipe end-to-end.
// ---------------------------------------------------------------------------

import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import type { Candidate, BrainstormQuery } from '../repository'

export interface BrainstormCtaProps {
  /** BrainstormQuery entry tag — mock provider dispatches candidates on this. */
  entry: BrainstormQuery['entry']
  /** Visible button text. Callers pass their locale-appropriate copy. */
  label: string
  /** Optional query extras passed through to the provider. */
  semanticType?: string
  pattern?: string
  dsId?: string
  intent?: string
  /**
   * Optional custom onApply. When omitted, the default stub
   * toasts the candidate's narrative headline and logs a debug trace.
   * Entry wrappers typically omit this so the default fires.
   */
  onApply?: (candidate: Candidate) => void
  /**
   * Additional CSS classes merged into the button — lets entry wrappers
   * fit visually into their mount surface (rail button, inline chip,
   * composer pill, etc.) without forking the primitive.
   */
  className?: string
}

function defaultApplyStub(entry: BrainstormQuery['entry']) {
  return (candidate: Candidate) => {
    const headline = candidate.narrativeHeadline || candidate.id
    toast.success(`Selected: ${headline}`)
    // eslint-disable-next-line no-console
    console.debug('[brainstorm]', entry, 'applied candidate', {
      candidateId: candidate.id,
      exampleId: candidate.exampleId,
      dsId: candidate.dsId,
      patternId: candidate.patternId,
    })
  }
}

export function BrainstormCta({
  entry,
  label,
  semanticType,
  pattern,
  dsId,
  intent,
  onApply,
  className = '',
}: BrainstormCtaProps) {
  const openBrainstorm = useStudioStore((s) => s.openBrainstorm)
  const effectiveOnApply = onApply ?? defaultApplyStub(entry)

  return (
    <button
      type="button"
      data-brainstorm-entry={entry}
      onClick={() =>
        openBrainstorm({
          query: { entry, semanticType, pattern, dsId, intent },
          onApply: effectiveOnApply,
        })
      }
      className={
        className ||
        'inline-flex h-10 items-center rounded-md border border-border px-3 text-xs font-medium transition-colors hover:bg-foreground/5'
      }
    >
      {label}
    </button>
  )
}
